import posthog from "posthog-js";
import { config } from "./config";
import { hasPrivacyOptOutSignal } from "./privacyControls";
import {
  DEFAULT_FUNNEL_CONCEPT,
  getFunnelConceptFromLocation,
  getStoredOrDefaultFunnelConcept,
  type FunnelConceptKey
} from "./funnelConcept";
import {
  buildCheckoutCompletePageviewPayload,
  buildFunnelPageviewPayload,
  type FunnelRouteOptions
} from "./funnelRoutes";
import { getOrCreateLandingAttribution, toAnalyticsLandingAttribution } from "./attribution";

type FbqFunction = (
  command: "init" | "track" | "set",
  eventNameOrPixelIdOrSetKey: string,
  parameters?: Record<string, unknown>,
  options?: Record<string, unknown>
) => void;

type FbqWithQueue = FbqFunction & {
  callMethod?: FbqFunction;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
};

type WindowWithMeta = Window &
  typeof globalThis & {
    fbq?: FbqWithQueue;
    _fbq?: unknown;
    /** Avoid duplicate `fbq('init')` when `initAnalytics` runs twice (e.g. React StrictMode dev). */
    __linkhivMetaPixelInit?: boolean;
  };

export type TrackingContext = {
  eventId: string;
  fbp: string | null;
  fbc: string | null;
  externalId: string;
};

/** Maximum checkout-path grace period for a cold Pixel `_fbp` cookie. Existing cookies return immediately. */
export const CHECKOUT_FBP_WAIT_MS = 200;

const PENDING_CHECKOUT_STORAGE_KEY = "linkhiv:pending-checkout";
const TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY = "linkhiv:tracked-purchase-events";
const LAST_FUNNEL_PAGEVIEW_URL_KEY = "__linkhiv_last_funnel_pageview_url__";
const META_EXTERNAL_ID_STORAGE_KEY = "linkhiv:meta-external-id";
const META_FBP_STORAGE_KEY = "linkhiv:meta-fbp";
const META_FBC_STORAGE_KEY = "linkhiv:meta-fbc";
const META_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const FUNNEL_STORAGE_KEY = "linkhiv:funnel:v2";

type WindowWithFunnelPv = Window &
  typeof globalThis & {
    [LAST_FUNNEL_PAGEVIEW_URL_KEY]?: string;
  };

/**
 * PostHog is initialized in `main.tsx` when `VITE_POSTHOG_KEY` is set.
 *
 * Meta side:
 * - Captures `fbclid` / mirrors `_fbp`/`_fbc` to localStorage on every load (cheap, idempotent).
 * - Loads `fbevents.js` once and runs `fbq('init', …)` + advanced-matching `external_id`.
 * - Does **not** fire `fbq('track', 'PageView')` here: `App.tsx` owns the PageView fire so
 *   the Pixel + CAPI share an `eventID` for dedup.
 */
export const initAnalytics = (): void => {
  if (typeof window === "undefined") return;
  if (hasPrivacyOptOutSignal()) return;

  // Always run — fbclid → _fbc capture must work even if VITE_META_PIXEL_ID is unset locally.
  persistMetaIdentifiers();

  if (!config.metaPixelId) return;

  const w = window as WindowWithMeta;
  if (w.__linkhivMetaPixelInit) return;
  w.__linkhivMetaPixelInit = true;

  if (!w.fbq) {
    const fbq = ((command, eventNameOrPixelId, parameters, options) => {
      if (fbq.callMethod) {
        fbq.callMethod(command, eventNameOrPixelId, parameters, options);
      } else {
        fbq.queue?.push([command, eventNameOrPixelId, parameters, options]);
      }
    }) as FbqWithQueue;
    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = "2.0";
    w.fbq = fbq;
    w._fbq = fbq;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  // The Pixel auto-hashes `external_id` for advanced matching; server CAPI also sends a
  // SHA-256 of the same raw value so Meta can join browser + server signals for the same user.
  w.fbq?.("init", config.metaPixelId, { external_id: getOrCreateExternalId() });

  // After fbevents.js loads it sets `_fbp` on the first event. Mirror to localStorage so we
  // survive cookie loss / private-mode resets across the funnel.
  scheduleFbpMirror();
};

export const track = (eventName: string, properties: Record<string, unknown> = {}): void => {
  if (config.posthogKey && !hasPrivacyOptOutSignal()) {
    posthog.capture(eventName, {
      product: "linkhiv",
      funnel: "linkhiv_webfunnel",
      ...properties,
      // Reapply the normalized snapshot last so callers cannot accidentally attach a
      // different, unvalidated creative ID to an otherwise comparable funnel event.
      ...getAnalyticsLandingAttributionProperties()
    });
  }
};

/** SPA “pageview” per funnel screen so PostHog paths and funnels match `#` routes. */
export const captureFunnelScreenPageview = (
  step: number,
  pendingFeedbackQuestionId: string | null,
  extras: { recommendedPlan: string; entryConceptKey: FunnelConceptKey; entryConceptLabel: string },
  options?: FunnelRouteOptions
): void => {
  if (!config.posthogKey || typeof window === "undefined" || hasPrivacyOptOutSignal()) return;
  const payload = buildFunnelPageviewPayload(step, pendingFeedbackQuestionId, extras, options);
  const w = window as WindowWithFunnelPv;
  if (w[LAST_FUNNEL_PAGEVIEW_URL_KEY] === payload.$current_url) return;
  w[LAST_FUNNEL_PAGEVIEW_URL_KEY] = String(payload.$current_url);
  posthog.capture("$pageview", {
    ...payload,
    ...getAnalyticsLandingAttributionProperties()
  });
};

export const captureCheckoutCompletePageview = (options?: FunnelRouteOptions): void => {
  if (!config.posthogKey || typeof window === "undefined" || hasPrivacyOptOutSignal()) return;
  const concept = getCurrentFunnelConcept();
  const payload = buildCheckoutCompletePageviewPayload({
    entryConceptKey: concept.key,
    entryConceptLabel: concept.label
  }, options);
  const w = window as WindowWithFunnelPv;
  if (w[LAST_FUNNEL_PAGEVIEW_URL_KEY] === payload.$current_url) return;
  w[LAST_FUNNEL_PAGEVIEW_URL_KEY] = String(payload.$current_url);
  posthog.capture("$pageview", {
    ...payload,
    ...getAnalyticsLandingAttributionProperties()
  });
};

export const registerFunnelPersonProperties = (properties: Record<string, string | number | boolean>): void => {
  if (!config.posthogKey || hasPrivacyOptOutSignal()) return;
  posthog.register(properties);
};

const getAnalyticsLandingAttributionProperties = (): Record<string, string> =>
  toAnalyticsLandingAttribution(getOrCreateLandingAttribution()) ?? {};

const getCurrentFunnelConcept = () => {
  if (typeof window === "undefined") return DEFAULT_FUNNEL_CONCEPT;
  const conceptFromUrl = getFunnelConceptFromLocation(window.location);
  if (conceptFromUrl) return conceptFromUrl;

  try {
    const raw = window.localStorage.getItem(FUNNEL_STORAGE_KEY);
    if (!raw) return DEFAULT_FUNNEL_CONCEPT;
    const parsed = JSON.parse(raw) as { entryConceptKey?: string };
    return getStoredOrDefaultFunnelConcept(parsed.entryConceptKey);
  } catch {
    return DEFAULT_FUNNEL_CONCEPT;
  }
};

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name: string, value: string): void => {
  if (typeof document === "undefined") return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const domain =
    hostname === "linkhiv.com" || hostname === "www.linkhiv.com" || hostname === "start.linkhiv.com"
      ? "; Domain=linkhiv.com"
      : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${META_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${domain}${secure}`;
};

const readStorage = (key: string): string | null => {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(key);
    return value?.trim() || null;
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // No-op if storage is unavailable (private mode / blocked storage).
  }
};

const mirrorFbp = (fbp: string): void => {
  setCookie("_fbp", fbp);
  writeStorage(META_FBP_STORAGE_KEY, fbp);
};

const buildMetaClickId = (fbclid: string): string => `fb.1.${Date.now()}.${fbclid}`;

const persistMetaIdentifiers = (): void => {
  if (typeof window === "undefined") return;

  const fbclid = new URL(window.location.href).searchParams.get("fbclid")?.trim();
  if (fbclid) {
    const fbc = buildMetaClickId(fbclid);
    setCookie("_fbc", fbc);
    writeStorage(META_FBC_STORAGE_KEY, fbc);
  }

  const fbpFromCookie = getCookie("_fbp");
  if (fbpFromCookie) {
    mirrorFbp(fbpFromCookie);
  }

  const fbcFromCookie = getCookie("_fbc");
  if (fbcFromCookie) {
    writeStorage(META_FBC_STORAGE_KEY, fbcFromCookie);
  }
};

/**
 * `fbevents.js` writes `_fbp` *after* it loads and processes the first event. Poll briefly so we
 * mirror it to localStorage and the first CAPI event (PageView) can still wait for fbp to land.
 */
const scheduleFbpMirror = (): void => {
  if (typeof window === "undefined") return;

  let attempts = 0;
  const tick = () => {
    const fbp = getCookie("_fbp");
    if (fbp) {
      mirrorFbp(fbp);
      return;
    }
    if (attempts++ < 20) {
      window.setTimeout(tick, 150);
    }
  };
  window.setTimeout(tick, 150);
};

/**
 * Wait briefly for `_fbp` to be set by `fbevents.js`. Resolves with the value (cookie or
 * localStorage mirror) or `null` if it never appears within `timeoutMs`. Use this once per
 * session before firing the very first CAPI event (PageView) to lift fbp coverage on cold
 * loads. Later events (ViewContent / Lead / IC) fire after substantial user interaction so
 * fbp is already set — no need to await.
 */
export const waitForFbp = (timeoutMs = 1500, intervalMs = 100): Promise<string | null> => {
  if (hasPrivacyOptOutSignal()) return Promise.resolve(null);
  if (typeof window === "undefined") return Promise.resolve(null);
  const existing = getCookie("_fbp") ?? readStorage(META_FBP_STORAGE_KEY);
  if (existing) {
    mirrorFbp(existing);
    return Promise.resolve(existing);
  }

  return new Promise((resolve) => {
    const start = Date.now();
    const poll = () => {
      const fbp = getCookie("_fbp");
      if (fbp) {
        mirrorFbp(fbp);
        resolve(fbp);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        const fallback = readStorage(META_FBP_STORAGE_KEY);
        if (fallback) mirrorFbp(fallback);
        resolve(fallback);
        return;
      }
      window.setTimeout(poll, intervalMs);
    };
    window.setTimeout(poll, intervalMs);
  });
};

const generateEventId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const getFbq = (): FbqFunction | null => {
  if (hasPrivacyOptOutSignal()) return null;
  if (!config.metaPixelId || typeof window === "undefined") return null;
  const fbq = (window as WindowWithMeta).fbq;
  return typeof fbq === "function" ? fbq : null;
};

const getOrCreateExternalId = (): string => {
  if (hasPrivacyOptOutSignal()) return `anon-${generateEventId()}`;
  const existing = readStorage(META_EXTERNAL_ID_STORAGE_KEY);
  if (existing) return existing;
  const externalId = `anon-${generateEventId()}`;
  writeStorage(META_EXTERNAL_ID_STORAGE_KEY, externalId);
  return externalId;
};

/**
 * Promote the session `external_id` from `anon-<uuid>` to `lead-<id>` so subsequent
 * InitiateCheckout / Purchase events carry a stable cross-session identifier. Meta still
 * clusters earlier events to the same user via email / fbp / fbc, so it is safe to swap
 * mid-funnel. Re-call `fbq('init', PIXEL_ID, {...})` to merge the new value into the
 * Pixel's advanced-matching parameters (the documented Meta pattern for runtime updates).
 */
export const upgradeExternalIdToLead = (leadId: number): void => {
  if (hasPrivacyOptOutSignal()) return;
  if (!Number.isInteger(leadId) || leadId <= 0) return;
  const upgraded = `lead-${leadId}`;
  if (readStorage(META_EXTERNAL_ID_STORAGE_KEY) === upgraded) return;
  writeStorage(META_EXTERNAL_ID_STORAGE_KEY, upgraded);
  if (config.metaPixelId) {
    getFbq()?.("init", config.metaPixelId, { external_id: upgraded });
  }
};

export const getTrackingContext = (): TrackingContext => ({
  eventId: generateEventId(),
  fbp: hasPrivacyOptOutSignal() ? null : getCookie("_fbp") ?? readStorage(META_FBP_STORAGE_KEY),
  fbc: hasPrivacyOptOutSignal() ? null : getCookie("_fbc") ?? readStorage(META_FBC_STORAGE_KEY),
  externalId: getOrCreateExternalId()
});

/**
 * Browser Pixel PageView; pair with `trackPageViewServerEvent` using the same `eventId`
 * so Meta deduplicates the two PageView signals into one (parity for the CAPI coverage
 * diagnostic in Events Manager).
 */
export const firePageViewBrowser = (eventId: string): void => {
  const fbq = getFbq();
  if (!fbq) return;
  fbq("track", "PageView", {}, { eventID: eventId });
};

export type MetaViewContentPayload = {
  eventId: string;
  contentIds: string[];
  value: number;
  currency: string;
  contentName?: string;
};

/** Browser ViewContent; use the same `eventId` as `trackViewContentServerEvent` for Meta dedup with CAPI. */
export const trackMetaViewContent = (payload: MetaViewContentPayload): void => {
  const fbq = getFbq();
  if (!fbq) {
    console.warn("[meta-pixel] ViewContent not fired — fbq unavailable");
    return;
  }
  const currency = payload.currency.trim().toUpperCase();
  const parameters: Record<string, unknown> = {
    content_ids: payload.contentIds,
    content_type: "product",
    currency,
    value: payload.value
  };
  if (payload.contentName?.trim()) {
    parameters.content_name = payload.contentName.trim();
  }
  fbq("track", "ViewContent", parameters, { eventID: payload.eventId });
};

export type MetaLeadOptions = {
  /** Plain email; Pixel hashes for advanced matching (lowercase recommended). */
  email?: string;
};

export const trackMetaLead = (eventId: string, options?: MetaLeadOptions): void => {
  const fbq = getFbq();
  if (!fbq) {
    console.warn("[meta-pixel] Lead not fired — fbq unavailable");
    return;
  }
  const email = options?.email?.trim().toLowerCase();
  // Merge `em` into the Pixel's advanced-matching params via `init` (the documented Meta
  // pattern for runtime advanced-matching updates) so it propagates to every subsequent event.
  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && config.metaPixelId) {
    fbq("init", config.metaPixelId, { em: email });
  }
  fbq("track", "Lead", {}, { eventID: eventId });
};

/**
 * Browser InitiateCheckout: use the **same** `eventId` as `createCheckoutSession` / worker CAPI
 * so Meta deduplicates browser + server into one InitiateCheckout.
 */
export const trackMetaInitiateCheckout = (
  eventId: string,
  planId: string,
  value: number,
  currency: string
): void => {
  const fbq = getFbq();
  if (!fbq) {
    console.warn(`[meta-pixel] InitiateCheckout not fired — fbq unavailable (event_id=${eventId})`);
    return;
  }
  fbq(
    "track",
    "InitiateCheckout",
    {
      content_ids: [planId],
      content_type: "product",
      currency: currency.trim().toUpperCase(),
      value
    },
    { eventID: eventId }
  );
};

const readTrackedPurchaseEventIds = (): string[] => {
  try {
    const raw = sessionStorage.getItem(TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === "string") : [];
  } catch {
    return [];
  }
};

export const trackMetaPurchase = (
  eventId: string,
  planId: string,
  value: number,
  currency: string
): boolean => {
  if (readTrackedPurchaseEventIds().includes(eventId)) {
    return true;
  }

  const fbq = getFbq();
  if (!fbq) {
    console.warn(`[meta-pixel] Purchase not fired — fbq unavailable (event_id=${eventId})`);
    return false;
  }

  console.log(`[meta-pixel] Dispatching Purchase (event_id=${eventId})`);
  fbq(
    "track",
    "Purchase",
    {
      content_ids: [planId],
      content_type: "product",
      currency: currency.trim().toUpperCase(),
      value
    },
    { eventID: eventId }
  );
  sessionStorage.setItem(
    TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY,
    JSON.stringify([...readTrackedPurchaseEventIds(), eventId])
  );
  return true;
};

export const rememberPendingCheckoutTracking = (tracking: {
  sessionId: string;
  eventId: string;
  planId: string;
  value: number;
  currency: string;
  completionToken?: string;
}): void => {
  sessionStorage.setItem(
    PENDING_CHECKOUT_STORAGE_KEY,
    JSON.stringify({ ...tracking, createdAt: Date.now() })
  );
};

export const getPendingCheckoutTracking = (sessionId: string): {
  eventId: string;
  planId: string;
  value: number;
  currency: string;
  completionToken?: string;
} | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_CHECKOUT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || parsed.sessionId !== sessionId) return null;
    return parsed;
  } catch {
    return null;
  }
};
